import { useEffect, useState } from "react";
import { api, fmtNGN } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, Clock, CheckCircle2, XCircle, RotateCcw, ArrowDownRight, ArrowUpRight,
  Landmark, Receipt,
} from "lucide-react";

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30", Icon: Clock },
  approved: { label: "Processing", cls: "bg-blue-500/15 text-blue-600 border-blue-500/30", Icon: Loader2 },
  processing: { label: "Processing", cls: "bg-blue-500/15 text-blue-600 border-blue-500/30", Icon: Loader2 },
  successful: { label: "Success", cls: "bg-green-500/15 text-green-600 border-green-500/30", Icon: CheckCircle2 },
  completed: { label: "Success", cls: "bg-green-500/15 text-green-600 border-green-500/30", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-destructive/15 text-destructive border-destructive/30", Icon: XCircle },
  reversed: { label: "Reversed", cls: "bg-destructive/15 text-destructive border-destructive/30", Icon: RotateCcw },
  rejected: { label: "Rejected", cls: "bg-destructive/15 text-destructive border-destructive/30", Icon: XCircle },
};

const labelFor = (t) => ({
  welcome_cashback: "Welcome Cash-back", daily_review: "Daily Review", referral: "Referral Reward",
  withdrawal: "Withdrawal", topup: "Top-up", purchase: "Artwork Purchase", promotional: "Promotional Reward",
  auction_sale: "Auction Sale", auction_capital: "Auction Capital", auction_profit: "Auction Profit",
  opt_out: "Opt-out Withdrawal", leader_bonus: "Leader Bonus",
  MEMBERSHIP_CASHBACK: "Welcome Cash-back", GROWTH_WALLET_FUNDING: "Growth Wallet Funding",
  WITHDRAWAL_REQUEST: "Withdrawal", WITHDRAWAL_SETTLEMENT: "Withdrawal Settlement",
  REVERSAL: "Payout Reversal", unapproved_withdrawal: "Unapproved Withdrawal — Refunded",
}[t] || t);

function StatusBadge({ status }) {
  const s = STATUS[status] || { label: status || "—", cls: "bg-secondary text-muted-foreground border-border", Icon: Clock };
  const spin = status === "processing" || status === "approved";
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border ${s.cls}`}>
      <s.Icon className={`h-3 w-3 ${spin ? "animate-spin" : ""}`} /> {s.label}
    </span>
  );
}

export default function Transactions() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/wallet/activity").then((r) => setData(r.data)).catch(() => setData({ withdrawals: [], transactions: [] }));
  }, []);

  if (!data) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const { withdrawals = [], transactions = [] } = data;

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold flex items-center gap-2"><Receipt className="h-7 w-7 text-gold" /> Transactions</h1>
        <p className="text-muted-foreground text-sm mt-1">Track the live status of your withdrawals and every wallet movement.</p>
      </div>

      <Tabs defaultValue="withdrawals">
        <TabsList className="rounded-full">
          <TabsTrigger value="withdrawals" data-testid="tab-withdrawals" className="rounded-full">Withdrawals ({withdrawals.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-tx" className="rounded-full">All Transactions ({transactions.length})</TabsTrigger>
        </TabsList>

        {/* Withdrawal status */}
        <TabsContent value="withdrawals" className="mt-5 space-y-3">
          {withdrawals.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-8 text-center text-muted-foreground" data-testid="no-withdrawals">
              <Landmark className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No withdrawal requests yet.
            </div>
          ) : withdrawals.map((w) => (
            <div key={w.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`withdrawal-card-${w.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-heading text-xl font-bold">{fmtNGN(w.requested)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Net payout {fmtNGN(w.net_amount)} · fee {fmtNGN(w.platform_fee)} · tax {fmtNGN(w.tax)}</p>
                </div>
                <StatusBadge status={w.status} />
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Landmark className="h-3 w-3" /> {w.bank_account ? `${w.bank_account.bank_name} • ${w.bank_account.account_number_masked}` : "—"}</span>
                <span>{new Date(w.created_at).toLocaleString()}</span>
              </div>
              {(w.status === "failed" || w.status === "reversed") && w.last_payout_error && (
                <p className="text-[11px] text-destructive mt-2" data-testid={`withdrawal-error-${w.id}`}>Reason: {w.last_payout_error}</p>
              )}
            </div>
          ))}
        </TabsContent>

        {/* All transactions */}
        <TabsContent value="all" className="mt-5 space-y-2">
          {transactions.length === 0 ? (
            <div className="bg-card border border-border rounded-2xl p-8 text-center text-muted-foreground" data-testid="no-transactions">No transactions yet.</div>
          ) : transactions.map((t) => (
            <div key={t.id} className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`tx-card-${t.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`h-9 w-9 rounded-full grid place-items-center shrink-0 ${t.amount >= 0 ? "bg-gold/15" : "bg-secondary"}`}>
                  {t.amount >= 0 ? <ArrowDownRight className="h-4 w-4 text-gold" /> : <ArrowUpRight className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{labelFor(t.type)}</p>
                  <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()} · <span className="capitalize">{t.wallet}</span> wallet</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className={`font-bold ${t.amount >= 0 ? "text-gold" : ""}`}>{t.amount >= 0 ? "+" : ""}{fmtNGN(t.amount)}</span>
                <StatusBadge status={t.status} />
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
